<template>
  <div class="animated fadeIn">
    <loader :isVisible="loading" />
    <ClientsProspectsMapFilters
      type="client"
      :initial-filter="initialFilter"
      @applyFilters="applyFilters"
    />

    <tabs v-model="activeTab" v-if="!loading && clients">
      <tab header="Départements">
        <StatsFranceMap :data="mapData" stats="clients" color-scheme="green" />
      </tab>
      <tab header="Marqueurs">
        <ClientProspectMarkersMap
          :data="clients"
          :filters="filters"
          type="clients"
        />
      </tab>
    </tabs>
  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters } from "vuex";

import Loader from "@/components/utils/Loader";
import Tabs from "vue-strap/src/Tabs";
import Tab from "vue-strap/src/Tab";
import ClientsProspectsMapFilters from "@/components/client/ClientsProspectsMapFilters";
import StatsFranceMap from "@/components/stats/StatsFranceMap";
import ClientProspectMarkersMap from "@/components/client/ClientProspectMarkersMap";

export default {
  components: {
    Loader,
    Tabs,
    Tab,
    ClientsProspectsMapFilters,
    StatsFranceMap,
    ClientProspectMarkersMap,
  },

  data() {
    return {
      loading: false,
      activeTab: 0,
      clients: null,
      filters: {
        begin: null,
        end: null,
        status: [],
      },
      initialFilter: {},
    };
  },

  computed: {
    ...mapState(["concepts"]),
    ...mapGetters({
      departments: "getDepartments",
      clientStatus: "getClientStatus",
    }),

    displayedDepartments() {
      const domTom = ["971", "972", "973", "974", "976"];
      const metroDepartments = this.departments.filter(
        (dpt) => !domTom.includes(dpt.code) && dpt.code !== "all",
      );

      return metroDepartments.map(({ code, name }) => {
        if (code === "all") {
          return { label: name, value: code };
        } else {
          return { label: code + " - " + name, value: code };
        }
      });
    },

    mapData() {
      const defaultData = {
        total: 0,
        totalPercentage: 0,
        reasons: [],
      };

      const departments = [
        ...this.displayedDepartments.map((dpt) => ({
          ...dpt,
          data: defaultData,
        })),
      ];

      if (!this.loading && this.clients) {
        departments.forEach((dpt) => {
          dpt.data = this.getDepartmentClientInfo(dpt.value);
        });
      }

      return departments;
    },
  },

  async created() {
    if (
      this.$route.query.begin ||
      this.$route.query.end ||
      this.$route.query.status
    ) {
      this.filters = {
        begin: this.$route.query.begin
          ? moment(this.$route.query.begin).toDate()
          : undefined,
        end: this.$route.query.end
          ? moment(this.$route.query.end).toDate()
          : undefined,
        status: this.$route.query.status
          ? Array.isArray(this.$route.query.status)
            ? this.$route.query.status
            : [this.$route.query.status]
          : [],
      };

      this.initialFilter = { ...this.filters };
      this.loadClients();
    } else {
      this.initialFilter = { ...this.filters };
    }
  },

  methods: {
    async loadClients() {
      try {
        this.loading = true;

        const { data } = await this.$api.get("/clients/map", {
          params: {
            begin: this.filters.begin,
            end: this.filters.end,
            status: this.filters.status,
          },
        });

        this.clients = data;
      } catch (e) {
        this.clients = [];
        this.$errorToast("Impossible de charger les clients");
      } finally {
        this.loading = false;
      }
    },

    getDepartmentClientInfo(dpt) {
      const departmentClients = this.clients.filter((client) => {
        if (
          client.zipcode &&
          typeof client.zipcode === "string" &&
          client.geoloc
        ) {
          return client.zipcode.slice(0, 2) === dpt;
        }

        return false;
      });

      const total = departmentClients.length;
      let totalPercentage = (total / this.clients.length) * 100;

      if (isNaN(totalPercentage)) {
        totalPercentage = 0;
      }

      const statuses = [];

      for (const client of departmentClients) {
        const status = statuses.find(({ value }) => value === client.status);

        if (status) {
          status.total = (status.total || 0) + 1;
        } else {
          statuses.push({
            value: client.status,
            label: this.getStatusLabel(client.status),
            color: this.getStatusColor(client.status),
            total: 1,
          });
        }
      }

      for (const status of statuses) {
        let percentage = (status.total / total) * 100;

        if (isNaN(percentage)) {
          percentage = 0;
        }

        status.percentage = percentage.toFixed(2);
        status.name = `${status.label} (${status.total}) (${status.percentage}%)`;
      }

      return { total, totalPercentage, reasons: statuses };
    },

    getStatusLabel(status) {
      const statusObj = this.clientStatus.find((s) => s.value === status);

      return statusObj ? statusObj.label : status;
    },

    getStatusColor(status) {
      const statusObj = this.clientStatus.find((s) => s.value === status);

      return statusObj ? statusObj.color : "#95a5a6";
    },

    applyFilters(form) {
      this.filters = {
        ...this.filters,
        ...form,
      };

      this.loadClients();
    },
  },
};
</script>
